import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Activity, ShieldCheck, Megaphone } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import { formatApiError } from "../lib/api";
import api from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const inviteToken = search.get("invite");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNeedsVerification(false);
    try {
      await login(email, password);
      // If we arrived via an invite link, send them straight to the accept page
      // (or directly into the program if accept succeeds).
      if (inviteToken) {
        try {
          const { data } = await api.post(`/invites/${inviteToken}/accept`);
          navigate(`/programs/${data.program_id}`);
          return;
        } catch {
          navigate(`/invite/${inviteToken}`);
          return;
        }
      }
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend signals "verify your email first" via a 403 + structured payload.
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setNeedsVerification(true);
        setError(detail.message || "Please verify your email before signing in.");
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      if (data?.email_sent === false) {
        setError("Re-send failed — our email provider is slow right now. Please try again in a minute.");
      } else {
        setError("Verification email re-sent. Check your inbox.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't re-send");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative array-grid-bg bg-slate-100 border-r border-slate-200">
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div>
            <img src="/array-logo.png" alt="Array Engine" className="h-9 w-auto" />
            <p className="label-overline mt-3">Reporting for Impact</p>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold text-slate-900">
              Turn program data into credible and useful reports
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Array Engine helps organisations monitor social impact programs by transforming data into clear insights — so you can track progress, understand outcomes, and report with confidence.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { Icon: Activity, label: "Track indicators", testid: "feature-track" },
                { Icon: ShieldCheck, label: "Validate data", testid: "feature-validate" },
                { Icon: Megaphone, label: "Communicate impact", testid: "feature-communicate" },
              ].map(({ Icon, label, testid }) => (
                <div
                  key={label}
                  className="border border-slate-200 bg-white p-4 rounded-sm flex flex-col items-start gap-3"
                  data-testid={testid}
                >
                  <span className="w-9 h-9 rounded-sm bg-emerald-50 border border-emerald-200 grid place-items-center">
                    <Icon className="w-4 h-4 text-emerald-700" strokeWidth={2} aria-hidden />
                  </span>
                  <div className="font-heading text-base leading-snug text-slate-900">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500">© Array Engine · Built for impact</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <p className="label-overline">Sign in</p>
          <h2 className="font-heading text-3xl font-semibold mt-2 flex items-center gap-2.5" data-testid="login-welcome-title">
            <span aria-hidden className="inline-block w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.18)]" />
            Welcome back
          </h2>
          <p className="text-sm text-slate-500 mt-1">Use your account to manage programs and reports.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="label-overline">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2" placeholder="john.doe@email.com" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="label-overline">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-2"
                testid="login-password-input"
              />
            </div>
            {error && (
              <div className={`border text-sm p-3 rounded-sm ${needsVerification ? "border-amber-200 bg-amber-50 text-amber-800" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="login-error">
                {error}
                {needsVerification && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResend}
                    disabled={resending || !email}
                    className="mt-2 ml-auto block text-amber-800 hover:text-amber-900 underline"
                    data-testid="login-resend-verification"
                  >
                    {resending ? "Re-sending…" : "Re-send verification email"}
                  </Button>
                )}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-slate-500 hover:text-slate-900 underline underline-offset-4" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 text-sm text-slate-600">
            New to Array Engine?{" "}
            <Link to="/register" className="underline underline-offset-4 text-slate-900" data-testid="register-link">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
